import { apiClient } from '../../api/client';

/** Итог из справочника (ТЗ §1.22) — чип в окне разговора. */
export interface OutcomeChip {
  id: number;
  name: string;
  code: string;
  action: 'recall_later' | 'booked' | null;
  color: 'ok' | 'warn' | 'danger' | 'neutral' | null;
}

export interface CallRow {
  id: number;
  at: string | null;
  direction: 'inbound' | 'outbound';
  client_phone: string;
  manager_id: number | null;
  line: { id: number; name: string; phone: string | null } | null;
  talk_sec: number | null;
  /** `s967`: с какого прозвона звонок — имя обзвона, «Входящий» или «Ручной набор». */
  dayler?: string | null;
  outcome: {
    auto: string | null;
    confirmed_by_manager: string | null;
    business_outcome_id: number | null;
    business_outcome: { id: number; name: string; code: string; color: string | null } | null;
    needs_confirm: boolean;
  };
  recall: {
    task_id: number;
    at: string | null;
    note: string | null;
    assigned_manager_id: number | null;
    status: string;
  } | null;
  quality: { badge: 'good' | 'warn' | 'bad' | 'unknown'; raw: Record<string, number> | null };
  transcript_status: string;
}

export interface CallCard {
  call: CallRow;
  recording: { id: number; duration_sec: number | null; tier: string; available_now: boolean } | null;
  transcript: { status: string; replicas: Array<{ role: string; t_from: number; t_to: number; text: string }> } | null;
  attempts: Array<{ at: string | null; result: string | null; ring_sec: number | null; sip_code: number | null }>;
}

/**
 * Начало сегодняшнего дня — дефолт периода журнала для обоих экранов (§4.4).
 *
 * Живёт рядом с запросами журнала, а не в компоненте: вкладка кабинета и
 * полный экран обязаны понимать «сегодня» одинаково.
 */
export function todayStart(): string {
  const from = new Date();
  from.setHours(0, 0, 0, 0);

  return from.toISOString();
}

export async function fetchOutcomes(): Promise<OutcomeChip[]> {
  const { data } = await apiClient.get<{ outcomes: OutcomeChip[] }>('/telephony/outcomes');

  return data.outcomes;
}

/**
 * История по номеру — то, что стоит столбцом справа от разговора (карта 2.4).
 *
 * Тот же журнал §4.4 с фильтром по цифрам номера: отдельной выборки «история
 * клиента» нет намеренно — два источника для одного и того же разъезжаются,
 * и в окне разговора оказывается не то, что в журнале.
 */
export async function fetchHistoryByPhone(phone: string, perPage = 10): Promise<CallRow[]> {
  return fetchHistory({ phone }, perPage);
}

/**
 * zhur1: история «того же клиента» ищется по ID опорного звонка — панель видит
 * номера краями, и фильтр по маске нашёл бы пустоту. Поиск по номеру остаётся
 * для ручного набора (номер известен локально, оператор сам его ввёл).
 */
export async function fetchHistory(
  key: { phone?: string; related_call_id?: number },
  perPage = 10,
): Promise<CallRow[]> {
  const { data } = await apiClient.get<{ rows: { data: CallRow[] } }>('/telephony/calls', {
    params: { ...key, per_page: perPage },
  });

  return data.rows.data ?? [];
}

export async function fetchCallCard(id: number): Promise<CallCard> {
  const { data } = await apiClient.get<CallCard>(`/telephony/calls/${id}`);

  return data;
}

/** Итог разговора: чип, ответ на вопрос неоднозначности, обещание перезвонить. */
export async function setCallOutcome(
  id: number,
  payload: {
    outcome_id?: number | null;
    confirm_manager?: 'confirmed_human' | 'was_machine';
    recall?: { at: string; note?: string; assign_to_me?: boolean };
  },
): Promise<CallCard> {
  const { data } = await apiClient.post<CallCard>(`/telephony/calls/${id}/outcome`, payload);

  return data;
}
